/**
 * The Command right rail: a tabbed inspector floating over the map.
 *
 * TRACK is the live detail of the selected aircraft. LAYERS drives the map's
 * layer visibility and render profile (consumed by the map controller). SIM
 * lists the simulated aircraft. COT and AI are declared but not yet connected —
 * they show their real status (not configured) rather than a faked panel.
 */

import { useEffect, useRef, useState } from 'react';

import { askAi, getAiStatus, getBriefing, triageFleet, type AiAlert, type AiStatus } from '@/api/ai';
import { useVoiceInput } from './useVoiceInput';
import { getCotStatus, type CotStatus } from '@/api/cot';
import { CotTlsStatusBlock } from './CotTlsStatus';
import { listActivity, type ActivityEntry } from '@/api/activity';
import { getLinks, type LinkHealth } from '@/api/links';
import { droneCommands } from '@/api/drones';
import type { ApiResult } from '@/api/client';
import { loadBasemap } from '@/api/basemap';
import { ExternalSourcesPanel } from './ExternalSourcesPanel';
import { OverlaysPanel } from './OverlaysPanel';
import { RemoteNodesPanel } from './RemoteNodesPanel';
import { useRemoteNodeLayer } from '@/map/useRemoteNodeLayer';
import { VideoTab } from './VideoTab';
import { ImpactTab } from './ImpactTab';
import { WeatherTab } from './WeatherTab';
import { getEndurance, type Endurance, type EnduranceVerdict } from '@/api/endurance';
import { getPreflight, type CheckStatus, type Readiness } from '@/api/preflight';
import { readParameter, writeParameter } from '@/api/parameters';
import { applyFailsafe, applyFleetFailsafe, getFailsafe } from '@/api/failsafe';
import {
  currentLine,
  doctrineNeedsConfirm,
  fleetStatusStyle,
  summarizeApply,
} from '@/features/command/failsafeLogic';
import { INDICATOR_LABEL } from '@/api/integrity';
import { useIntegrity } from '@/stores/integrityStore';
import { removeSimulator, spawnSimulators } from '@/api/sim';
import { teamColorFor } from '@/features/fleet/FleetRail';
import { cn } from '@/lib/utils';
import { AOR_RADIUS_KM, aorById } from '@/map/aors';
import {
  PACK_MAX_ZOOM,
  PACK_MIN_ZOOM,
  boundsAround,
  downloadPack,
  estimatePack,
  formatBytes,
  tilesForBounds,
  type PackProgress,
} from '@/map/offlinePack';
import { useMapController } from '@/map/MapCanvas';
import { useCanControl, useHasRole } from '@/stores/authStore';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import {
  useAutopilotMessages,
  useDroneIdentity,
  useDroneIds,
  useDroneMessage,
  useDroneScalars,
  useDroneStore,
  useLatestAutopilotWarning,
  usePrimarySelectedId,
} from '@/stores/droneStore';
import { LAYERS, useMapStore, type MapProfile } from '@/stores/mapStore';
import type {
  CommandResult,
  DroneId,
  FailsafeApplyResult,
  FailsafeDoctrine,
  FailsafeInfo,
  FleetFailsafeResult,
} from '@/types/domain';

type TabId = 'track' | 'layers' | 'fs' | 'cot' | 'sim' | 'ai' | 'log' | 'video' | 'search' | 'wx';
const TABS: Array<{ id: TabId; label: string }> = [
  { id: 'track', label: 'TRACK' },
  { id: 'layers', label: 'LAYERS' },
  { id: 'fs', label: 'FS' },
  { id: 'cot', label: 'COT' },
  { id: 'sim', label: 'SIM' },
  { id: 'ai', label: 'AI' },
  { id: 'log', label: 'LOG' },
  { id: 'video', label: 'VIDEO' },
  { id: 'search', label: 'SEARCH' },
  { id: 'wx', label: 'WX' },
];

function Toggle({ on, onClick, disabled }: { on: boolean; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={onClick}
      className="hit-44 relative h-[18px] w-8 shrink-0 rounded-[99px] transition-colors disabled:opacity-40"
      style={{ background: on ? 'var(--friend)' : 'oklch(1 0 0 / 0.15)' }}
    >
      <span
        className="absolute top-[2px] size-[14px] rounded-full bg-white transition-all"
        style={{ left: on ? 16 : 2 }}
      />
    </button>
  );
}

function Field({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="well flex flex-col gap-1 rounded-[11px] px-3 py-2">
      <span className="label-mono text-[8px] text-[color:var(--muted-foreground-2)]">{label}</span>
      <span className="tnum text-[12.5px]" style={tone ? { color: tone } : undefined}>
        {value}
      </span>
    </div>
  );
}

function TrackTab({ droneId }: { droneId: DroneId }) {
  const identity = useDroneIdentity(droneId);
  const scalars = useDroneScalars(droneId, [
    'lat',
    'lon',
    'altRelative',
    'altAmsl',
    'groundSpeed',
    'heading',
    'armed',
    'batteryPercent',
    'batteryVoltage',
    'gpsFixType',
    'gpsSatellites',
  ]);
  const rc = useDroneMessage(droneId, 'RC_CHANNELS');
  const rssi = typeof rc?.rssi === 'number' && rc.rssi !== 255 ? Math.round((rc.rssi / 254) * 100) : null;

  if (!identity) return null;
  const gpsOk = (scalars.gpsFixType ?? 0) >= 3;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <span className="size-2.5 rotate-45 rounded-[2px] border-2" style={{ borderColor: 'var(--friend)' }} />
        <span className="label-mono text-[9px]" style={{ color: 'var(--friend)' }}>
          FRIEND · OWN UAS
        </span>
        {scalars.armed ? (
          <span className="rounded-[99px] px-1.5 py-px label-mono text-[8px]" style={{ color: 'var(--armed)', background: 'color-mix(in oklch, var(--armed) 16%, transparent)' }}>
            ARMED
          </span>
        ) : null}
        {identity.simulated ? (
          <span className="rounded-[99px] px-1.5 py-px label-mono text-[8px]" style={{ color: 'var(--caution)', background: 'color-mix(in oklch, var(--caution) 14%, transparent)' }}>
            SIM
          </span>
        ) : null}
      </div>

      <div className="tnum text-[15px] font-semibold">{identity.callsign}</div>
      <div className="flex flex-wrap items-center gap-2">
        <span
          className="label-mono text-[8px]"
          style={{ color: identity.state === 'connected' ? 'var(--ok)' : 'var(--caution)' }}
        >
          ● LINK {(identity.state ?? 'unknown').toUpperCase()}
        </span>
        {/* Reports what the link is actually doing, never what was configured.
            A SIGNED badge on a link whose signing failed to engage is a false
            trust indicator, which is worse than showing nothing — so this is
            driven by the connection's live state, and simulated aircraft (which
            have no radio to authenticate) are excluded entirely. */}
        {identity.simulated ? null : (
          <span
            className="label-mono text-[8px]"
            title={
              identity.signing
                ? 'MAVLink 2 signing engaged: frames are authenticated with a shared key, and unsigned frames are rejected.'
                : 'This link is unauthenticated. Anyone within radio range who can transmit MAVLink can command this aircraft. Set MAVLINK_SIGNING_KEY to enable signing.'
            }
            style={{ color: identity.signing ? 'var(--ok)' : 'var(--caution)' }}
          >
            {identity.signing ? '⚿ MAV2 SIGNED' : '⚠ UNSIGNED LINK'}
          </span>
        )}
      </div>

      <AutopilotWarningBadge droneId={droneId} />
      <IntegrityPanel droneId={droneId} />
      <ReadinessPanel droneId={droneId} />
      <EndurancePanel droneId={droneId} />
      <BenchPanel droneId={droneId} />
      <LinkHealthPanel droneId={droneId} />
      <AutopilotMessagesPanel droneId={droneId} />

      <div className="grid grid-cols-2 gap-2">
        <Field label="LAT" value={scalars.lat === undefined ? '---' : scalars.lat.toFixed(5)} />
        <Field label="LON" value={scalars.lon === undefined ? '---' : scalars.lon.toFixed(5)} />
        <Field label="AGL" value={scalars.altRelative === undefined ? '---' : `${scalars.altRelative.toFixed(1)} m`} />
        <Field label="MSL" value={scalars.altAmsl === undefined ? '---' : `${scalars.altAmsl.toFixed(1)} m`} />
        <Field label="GROUND SPD" value={scalars.groundSpeed === undefined ? '---' : `${scalars.groundSpeed.toFixed(1)} m/s`} />
        <Field label="HEADING" value={scalars.heading === undefined ? '---' : `${scalars.heading.toFixed(0)}°`} />
        <Field
          label="BATTERY"
          tone={scalars.batteryPercent !== undefined && scalars.batteryPercent <= 30 ? 'var(--caution)' : undefined}
          value={scalars.batteryPercent === undefined ? '---' : `${scalars.batteryPercent.toFixed(0)}%${scalars.batteryVoltage === undefined ? '' : ` · ${scalars.batteryVoltage.toFixed(1)}V`}`}
        />
        <Field label="RSSI" value={rssi === null ? '---' : `${rssi}%`} />
        <Field label="STATE" value={identity.state.toUpperCase()} tone={identity.state !== 'connected' ? 'var(--danger-foreground)' : undefined} />
        <Field label="GPS" value={gpsOk ? `3D · ${scalars.gpsSatellites ?? 0} sats` : 'NO FIX'} tone={gpsOk ? undefined : 'var(--caution)'} />
      </div>
    </div>
  );
}

/**
 * Pull this AOR's tiles down before leaving signal.
 *
 * The service worker already serves anything already browsed; this covers the
 * case that actually strands people — arriving somewhere with no link and
 * needing part of the map nobody has opened yet.
 */
function OfflinePack() {
  const aor = aorById(useWorkspaceStore((s) => s.aor));
  const [progress, setProgress] = useState<PackProgress | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const abort = useRef<AbortController | null>(null);

  const bounds = boundsAround(aor.center[1], aor.center[0], AOR_RADIUS_KM);
  const estimate = estimatePack(bounds);
  const running = progress !== null && progress.done < progress.total;

  const start = () => {
    if (estimate.tooLarge) return;
    setMessage(null);
    const controller = new AbortController();
    abort.current = controller;
    const tiles = tilesForBounds(bounds);
    setProgress({ done: 0, total: tiles.length, failed: 0 });
    void loadBasemap()
      .then((basemap) =>
        downloadPack(tiles, setProgress, { signal: controller.signal, template: basemap.tileUrl }),
      )
      .then((final) => {
        if (controller.signal.aborted) {
          setMessage('Download stopped. Tiles already fetched stay available offline.');
          setProgress(null);
          return;
        }
        setMessage(
          final.failed
            ? `${aor.id} cached — ${final.failed} tile(s) unavailable, so the map has small gaps.`
            : `${aor.id} cached. This area now works with no link.`,
        );
      });
  };

  return (
    <div className="flex flex-col gap-2">
      <span className="label-mono text-[8px] text-[color:var(--muted-foreground-2)]">
        OFFLINE MAP — AOR {aor.id}
      </span>
      <div className="well flex flex-col gap-2 rounded-[11px] px-3 py-2.5">
        <span className="text-[10.5px] leading-relaxed text-[color:var(--muted-foreground-3)]">
          Caches z{PACK_MIN_ZOOM}–{PACK_MAX_ZOOM} over this AOR so the map keeps working with the
          radio off. About {formatBytes(estimate.bytes)} ({estimate.tiles.toLocaleString()} tiles).
        </span>
        {running ? (
          <>
            <div className="h-1.5 overflow-hidden rounded-full bg-black/35">
              <span
                className="block h-full transition-all"
                style={{
                  width: `${Math.round((progress.done / Math.max(1, progress.total)) * 100)}%`,
                  background: 'var(--ok)',
                }}
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="tnum text-[10px] text-[color:var(--muted-foreground-3)]">
                {progress.done} / {progress.total}
              </span>
              <button
                type="button"
                onClick={() => abort.current?.abort()}
                className="ml-auto rounded-[99px] border border-[color:var(--border)] px-2.5 py-1 label-mono text-[8.5px]"
              >
                STOP
              </button>
            </div>
          </>
        ) : (
          <button
            type="button"
            disabled={estimate.tooLarge}
            onClick={start}
            className="rounded-[99px] bg-[oklch(1_0_0/0.92)] py-2 label-mono text-[9px] text-[#0c0c0d] disabled:opacity-40"
          >
            ⇣ DOWNLOAD FOR OFFLINE
          </button>
        )}
        {message ? (
          <span className="text-[10px]" style={{ color: 'var(--ok)' }}>
            {message}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Navigation and link integrity for one aircraft.
 *
 * Worded as suspicion, not fact. A jammed or spoofed aircraft still reports
 * clean-looking telemetry, so the operator's own judgement is the last check —
 * the panel gives them the measurement that triggered it and says plainly that
 * the position may be a lie.
 */
const READINESS_TONE: Record<CheckStatus, string> = {
  pass: 'var(--ok)',
  warn: 'var(--caution)',
  fail: 'var(--danger-foreground)',
  unknown: 'var(--muted-foreground-3)',
};
const READINESS_MARK: Record<CheckStatus, string> = {
  pass: '✓',
  warn: '!',
  fail: '✕',
  unknown: '–',
};

/**
 * Pre-flight readiness: the checks an operator currently runs by eye, in one
 * verdict. Polled slowly — this is a go/no-go read before launch, not telemetry.
 *
 * A warn never blocks; it is the operator's call. Only a fail withholds the
 * clearance, and anything the aircraft has not reported shows as unknown rather
 * than passing, because silence is not health.
 */
function ReadinessPanel({ droneId }: { droneId: DroneId }) {
  const [readiness, setReadiness] = useState<Readiness | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      void getPreflight(droneId).then((result) => {
        if (!cancelled) setReadiness(result.ok ? result.data : null);
      });
    load();
    const timer = window.setInterval(load, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [droneId]);

  if (!readiness) return null;
  const failed = readiness.checks.filter((c) => c.status === 'fail').length;

  return (
    <div className="well flex flex-col gap-1.5 rounded-[11px] px-3 py-2.5">
      <div className="flex items-center gap-2">
        <span className="label-mono text-[8px] text-[color:var(--muted-foreground-2)]">
          PRE-FLIGHT
        </span>
        <span
          className="ml-auto label-mono text-[8px]"
          style={{ color: readiness.cleared ? 'var(--ok)' : 'var(--danger-foreground)' }}
        >
          {readiness.cleared ? '● CLEARED' : `● ${failed} BLOCKING`}
        </span>
      </div>
      {readiness.checks.map((check) => (
        <div key={check.key} className="flex items-center gap-2 text-[11px]">
          <span
            className="w-3 shrink-0 text-center font-mono text-[10px]"
            style={{ color: READINESS_TONE[check.status] }}
          >
            {READINESS_MARK[check.status]}
          </span>
          <span className="text-[color:var(--muted-foreground-2)]">{check.label}</span>
          <span className="tnum ml-auto" style={{ color: READINESS_TONE[check.status] }}>
            {check.detail}
          </span>
        </div>
      ))}
    </div>
  );
}

const ENDURANCE_TONE: Record<EnduranceVerdict, string> = {
  ok: 'var(--ok)',
  commit: 'var(--warn)',
  critical: 'var(--danger-foreground)',
  unknown: 'var(--muted-foreground-2)',
};

const ENDURANCE_HEAD: Record<EnduranceVerdict, string> = {
  ok: '● CAN RETURN',
  commit: '● COMMIT NOW',
  critical: '● CANNOT RETURN',
  unknown: '● NO ESTIMATE',
};

function clock(seconds: number | null): string {
  if (seconds === null) return '—';
  const total = Math.max(0, Math.round(seconds));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * The return-home margin.
 *
 * Separate from the battery readout on purpose. A percentage invites the wrong
 * comparison — the question is never "how full" but "is there enough left to
 * get back from here", and only one of those changes as the aircraft flies out.
 */
function EndurancePanel({ droneId }: { droneId: DroneId }) {
  const [endurance, setEndurance] = useState<Endurance | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      void getEndurance(droneId).then((result) => {
        if (!cancelled) setEndurance(result.ok ? result.data : null);
      });
    load();
    const timer = window.setInterval(load, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [droneId]);

  if (!endurance) return null;
  const tone = ENDURANCE_TONE[endurance.verdict];

  return (
    <div className="well flex flex-col gap-1.5 rounded-[11px] px-3 py-2.5">
      <div className="flex items-center gap-2">
        <span className="label-mono text-[8px] text-[color:var(--muted-foreground-2)]">
          RETURN MARGIN
        </span>
        <span className="ml-auto label-mono text-[8px]" style={{ color: tone }}>
          {ENDURANCE_HEAD[endurance.verdict]}
        </span>
      </div>
      <div className="flex items-center gap-2 text-[11px]">
        <span className="text-[color:var(--muted-foreground-2)]">MARGIN</span>
        <span className="tnum ml-auto text-[13px]" style={{ color: tone }}>
          {clock(endurance.marginS)}
        </span>
      </div>
      <div className="flex items-center gap-2 text-[11px]">
        <span className="text-[color:var(--muted-foreground-2)]">ENDURANCE</span>
        <span className="tnum ml-auto">{clock(endurance.enduranceS)}</span>
      </div>
      <div className="flex items-center gap-2 text-[11px]">
        <span className="text-[color:var(--muted-foreground-2)]">TO HOME</span>
        <span className="tnum ml-auto">{clock(endurance.homeS)}</span>
      </div>
      {endurance.reachBeyondHomeM !== null ? (
        <div className="flex items-center gap-2 text-[11px]">
          <span className="text-[color:var(--muted-foreground-2)]">CAN PUSH</span>
          <span className="tnum ml-auto">
            {endurance.reachBeyondHomeM >= 1000
              ? `${(endurance.reachBeyondHomeM / 1000).toFixed(1)} km`
              : `${Math.round(endurance.reachBeyondHomeM)} m`}
          </span>
        </div>
      ) : null}
      <p className="text-[10px] leading-snug text-[color:var(--muted-foreground-2)]">
        {endurance.reason}
      </p>
    </div>
  );
}

/**
 * Recent orders across the fleet, newest first.
 *
 * Deliberately shows who issued each one. On a shared console the useful
 * question mid-mission is rarely "what happened" but "did someone else already
 * do this" — two operators sending the same aircraft two different places is
 * the failure a feed without a name attached does nothing to prevent.
 */
function LogTab() {
  const [entries, setEntries] = useState<ActivityEntry[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      void listActivity(60).then((result) => {
        if (!cancelled) setEntries(result.ok ? result.data.entries : []);
      });
    load();
    const timer = window.setInterval(load, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  if (entries === null) return null;

  if (entries.length === 0) {
    return (
      <div className="well rounded-[11px] px-3 py-2.5">
        <p className="text-[11px] text-[color:var(--muted-foreground-2)]">
          No orders issued to your aircraft in the last hour.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      {entries.map((entry) => (
        <div key={entry.entryId} className="well flex flex-col gap-1 rounded-[11px] px-3 py-2">
          <div className="flex items-baseline gap-2">
            <span className="label-mono text-[8px] text-[color:var(--muted-foreground-2)]">
              {entry.droneId}
            </span>
            <span
              className="tnum ml-auto text-[9px] text-[color:var(--muted-foreground-2)]"
              title={new Date(entry.timestamp * 1000).toLocaleString()}
            >
              {new Date(entry.timestamp * 1000).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              })}
            </span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="break-all text-[11px]">{entry.detail}</span>
          </div>
          <span className="text-[9px] text-[color:var(--muted-foreground-2)]">
            {entry.username ?? 'unknown'} · {entry.outcome}
          </span>
        </div>
      ))}
    </div>
  );
}

const LINK_TONE: Record<LinkHealth, string> = {
  live: 'var(--ok)',
  stale: 'var(--warn)',
  dead: 'var(--danger-foreground)',
};

/**
 * The link carrying this aircraft, and its health.
 *
 * Honest about the single-link present: it shows the one transport and whether
 * it is live. When a second link is added for an aircraft, the same panel names
 * which is primary — the routing brain that chooses is already there.
 */
function LinkHealthPanel({ droneId }: { droneId: DroneId }) {
  const [primary, setPrimary] = useState<string | null>(null);
  const [health, setHealth] = useState<LinkHealth | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      void getLinks().then((result) => {
        if (cancelled || !result.ok) return;
        const entry = result.data.aircraft.find((a) => a.droneId === droneId);
        const link = entry?.links[0] ?? null;
        setPrimary(entry?.primaryLink ?? null);
        setHealth(link?.health ?? null);
      });
    load();
    const timer = window.setInterval(load, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [droneId]);

  if (health === null) return null;

  return (
    <div className="well flex items-center gap-2 rounded-[11px] px-3 py-2">
      <span className="label-mono text-[8px] text-[color:var(--muted-foreground-2)]">LINK</span>
      <span className="tnum truncate text-[10px]">{primary ?? 'none'}</span>
      <span className="label-mono ml-auto text-[8px]" style={{ color: LINK_TONE[health] }}>
        ● {health.toUpperCase()}
      </span>
    </div>
  );
}

/**
 * Ground-only maintenance: reboot the autopilot, spin a motor.
 *
 * Rendered only while the aircraft is disarmed, which is the point. Both
 * actions are refused server-side on an armed aircraft — that check is the
 * real one and lives in app/domain/maintenance.py — but a REBOOT button
 * sitting next to LAND during a flight is a misclick waiting for a bad day.
 * The safest control is the one that is not there.
 */
function BenchPanel({ droneId }: { droneId: DroneId }) {
  const armed = useDroneScalars(droneId, ['armed']).armed === true;
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [motor, setMotor] = useState(1);
  // Which motor the operator has already been asked about. The spin is two
  // clicks on purpose: the server refuses an unconfirmed motor test with a 409,
  // and this is the click that earns the confirmation rather than sending it
  // reflexively. Keyed by motor so changing the selection cancels the arm.
  const [armedMotor, setArmedMotor] = useState<number | null>(null);

  if (armed) return null;

  const run = async (what: string, call: () => Promise<ApiResult<CommandResult>>) => {
    setBusy(true);
    setNote(null);
    const result = await call();
    setBusy(false);
    setNote(result.ok ? `${what} accepted` : result.error.message);
  };

  return (
    <div className="well flex flex-col gap-2 rounded-[11px] px-3 py-2.5">
      <span className="label-mono text-[8px] text-[color:var(--muted-foreground-2)]">
        BENCH · DISARMED ONLY
      </span>
      <div className="flex items-center gap-1.5">
        <select
          value={motor}
          onChange={(event) => {
            setMotor(Number(event.target.value));
            setArmedMotor(null);
          }}
          aria-label="Motor"
          className="well rounded-[7px] px-1.5 py-1 font-mono text-[10px] outline-none"
        >
          {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
            <option key={n} value={n}>
              M{n}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={busy}
          title="Spin this motor at 5% for 2 seconds. Check it turns, and turns the right way."
          onClick={() => {
            if (armedMotor !== motor) {
              setArmedMotor(motor);
              setNote(`Clear the propellers. Press again to spin M${motor}.`);
              return;
            }
            setArmedMotor(null);
            void run(`Motor ${motor}`, () => droneCommands.testMotor(droneId, motor, 5, 2));
          }}
          className="well flex-1 rounded-[99px] px-2 py-1 label-mono text-[9px] disabled:opacity-40"
          style={armedMotor === motor ? { color: 'var(--warn)' } : undefined}
        >
          {armedMotor === motor ? `CONFIRM · SPIN M${motor}` : 'SPIN 5% · 2S'}
        </button>
      </div>
      <button
        type="button"
        disabled={busy}
        title="Restart the flight controller. The link drops and the mission is cleared from RAM."
        onClick={() => void run('Reboot', () => droneCommands.reboot(droneId))}
        className="well rounded-[99px] px-2 py-1 label-mono text-[9px] disabled:opacity-40"
        style={{ color: 'var(--warn)' }}
      >
        REBOOT AUTOPILOT
      </button>
      {note ? (
        <p className="text-[10px] leading-snug text-[color:var(--muted-foreground-2)]">{note}</p>
      ) : null}
    </div>
  );
}

/**
 * The autopilot's own words, coloured by how loud they are.
 *
 * emergency/alert/critical/error (severity <= 3) are red, warning (4) amber,
 * and notice/info/debug muted — so a PreArm failure reads at a glance and a
 * routine "using GPS" does not compete with it.
 */
function autopilotTone(severity: number): string {
  if (severity <= 3) return 'var(--danger-foreground)';
  if (severity === 4) return 'var(--caution)';
  return 'var(--muted-foreground-2)';
}

function autopilotTime(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * The latest warning-or-worse line, surfaced next to the aircraft's status.
 *
 * A PreArm failure buried in a panel nobody opened is a failure the operator
 * never sees, so the worst recent line is lifted out here. Silent when the
 * recent chatter is all informational.
 */
function AutopilotWarningBadge({ droneId }: { droneId: DroneId }) {
  const warning = useLatestAutopilotWarning(droneId);
  if (!warning) return null;
  const tone = autopilotTone(warning.severity);

  return (
    <div
      className="flex items-start gap-2 rounded-[10px] border px-2.5 py-1.5"
      style={{
        borderColor: `color-mix(in oklch, ${tone} 45%, transparent)`,
        background: `color-mix(in oklch, ${tone} 8%, transparent)`,
      }}
    >
      <span className="label-mono mt-px shrink-0 text-[8px]" style={{ color: tone }}>
        {warning.level.toUpperCase()}
      </span>
      <span className="min-w-0 flex-1 break-words text-[10.5px] leading-snug">{warning.text}</span>
    </div>
  );
}

/**
 * The autopilot message log for the selected aircraft: STATUSTEXT, newest
 * first, severity-coloured and timestamped. The one place the whole calibration
 * or pre-arm dialogue is legible, not just the last line.
 */
function AutopilotMessagesPanel({ droneId }: { droneId: DroneId }) {
  const messages = useAutopilotMessages(droneId);

  return (
    <div className="well flex flex-col gap-1.5 rounded-[11px] px-3 py-2.5">
      <div className="flex items-center gap-2">
        <span className="label-mono text-[8px] text-[color:var(--muted-foreground-2)]">
          AUTOPILOT MESSAGES
        </span>
        {messages.length ? (
          <span className="tnum ml-auto label-mono text-[8px] text-[color:var(--muted-foreground-3)]">
            {messages.length}
          </span>
        ) : null}
      </div>
      {messages.length === 0 ? (
        <p className="text-[10px] leading-snug text-[color:var(--muted-foreground-3)]">
          Nothing said yet. The aircraft's own status lines appear here as it sends them.
        </p>
      ) : (
        <ul className="scroll-thin flex max-h-56 flex-col gap-1 overflow-y-auto">
          {messages.map((message, index) => {
            const tone = autopilotTone(message.severity);
            return (
              <li
                key={`${message.timestamp}-${index}`}
                className="flex items-start gap-2 font-mono text-[10.5px] leading-snug"
              >
                <span
                  className="tnum shrink-0 text-[9px] text-[color:var(--muted-foreground-3)]"
                  title={new Date(message.timestamp * 1000).toLocaleString()}
                >
                  {autopilotTime(message.timestamp)}
                </span>
                <span className="min-w-0 flex-1 break-words" style={{ color: tone }}>
                  {message.text}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function IntegrityPanel({ droneId }: { droneId: DroneId }) {
  const findings = useIntegrity(droneId);
  if (findings.length === 0) return null;

  const critical = findings.some((f) => f.severity === 'critical');
  const tone = critical ? 'var(--danger-foreground)' : 'var(--caution)';

  return (
    <div
      className="flex flex-col gap-1.5 rounded-[12px] border px-3 py-2.5"
      style={{
        borderColor: `color-mix(in oklch, ${tone} 45%, transparent)`,
        background: `color-mix(in oklch, ${tone} 8%, transparent)`,
      }}
    >
      <span className="label-mono text-[8.5px]" style={{ color: tone }}>
        ⚠ INTEGRITY — {critical ? 'POSITION SUSPECT' : 'DEGRADED'}
      </span>
      {findings.map((finding, index) => (
        <div key={index} className="flex flex-col gap-0.5">
          <span className="label-mono text-[8px]" style={{ color: tone }}>
            {INDICATOR_LABEL[finding.indicator] ?? finding.indicator.toUpperCase()}
          </span>
          <span className="text-[10.5px] leading-relaxed text-[color:var(--muted-foreground-2)]">
            {finding.detail}
          </span>
        </div>
      ))}
    </div>
  );
}

function LayersTab() {
  const profile = useMapStore((s) => s.profile);
  const setProfile = useMapStore((s) => s.setProfile);
  const terrainOn = useMapStore((s) => s.terrainOn);
  const setTerrainOn = useMapStore((s) => s.setTerrainOn);
  const terrain = useMapStore((s) => s.terrainExaggeration);
  const setTerrain = useMapStore((s) => s.setTerrain);
  const layers = useMapStore((s) => s.layers);
  const toggleLayer = useMapStore((s) => s.toggleLayer);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <span className="label-mono text-[8px] text-[color:var(--muted-foreground-2)]">RENDER PROFILE</span>
        <div className="well flex rounded-[99px] p-1">
          {(['3d', '2d'] as MapProfile[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setProfile(m)}
              className={cn(
                'flex-1 rounded-[99px] py-1.5 label-mono text-[9px] transition-colors',
                profile === m ? 'bg-[oklch(1_0_0/0.92)] text-[#0c0c0d]' : 'text-[color:var(--muted-foreground-3)]',
              )}
            >
              {/* "2D FLAT", not "2D OFFLINE": the projection switch never made
                  tiles offline. The service worker does that now, for any area
                  already viewed, in either projection. */}
              {m === '3d' ? '3D GLOBE' : '2D FLAT'}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <span className="text-[12px]">3D terrain</span>
          <span className="label-mono text-[8px] text-[color:var(--muted-foreground-2)]">
            {profile === '2d' ? 'N/A IN 2D' : terrainOn ? '' : 'FLAT · FAST'}
          </span>
          <span className="ml-auto">
            <Toggle on={terrainOn} disabled={profile === '2d'} onClick={() => setTerrainOn(!terrainOn)} />
          </span>
        </div>
        <label className="flex flex-col gap-2">
          <span className="label-mono text-[8px] text-[color:var(--muted-foreground-2)]">
            EXAGGERATION · {terrain.toFixed(1)}×
          </span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.1}
            value={terrain}
            onChange={(e) => setTerrain(Number(e.target.value))}
            disabled={profile === '2d' || !terrainOn}
            className="accent-[color:var(--friend)] disabled:opacity-40"
          />
        </label>
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="label-mono text-[8px] text-[color:var(--muted-foreground-2)]">LAYERS</span>
        {LAYERS.map((layer) => (
          <div key={layer.key} className="flex items-center gap-2 py-0.5">
            <span className={cn('text-[12px]', !layer.available && 'text-[color:var(--muted-foreground)]')}>
              {layer.label}
            </span>
            {!layer.available ? (
              <span className="label-mono text-[8px] text-[color:var(--muted-foreground-2)]">NO FEED</span>
            ) : null}
            <span className="ml-auto">
              <Toggle on={layers[layer.key]} disabled={!layer.available} onClick={() => toggleLayer(layer.key)} />
            </span>
          </div>
        ))}
      </div>

      <OfflinePack />
    </div>
  );
}

const SIM_TYPES = [
  { value: 'quad', label: 'QUAD — multirotor' },
  { value: 'plane', label: 'PLANE — fixed wing' },
  { value: 'rover', label: 'ROVER — ground' },
  { value: 'boat', label: 'BOAT — surface' },
] as const;

function SimTab() {
  const ids = useDroneIds();
  const isAdmin = useHasRole('admin');
  const aor = aorById(useWorkspaceStore((s) => s.aor));
  const [vehicleType, setVehicleType] = useState('quad');
  const [count, setCount] = useState('1');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ text: string; warn: boolean } | null>(null);

  const n = Number.parseInt(count, 10);
  const countValid = Number.isFinite(n) && n >= 1 && n <= 10;

  const spawn = () => {
    if (!countValid || busy) return;
    setBusy(true);
    // Spawn slightly off the AOR centre so new arrivals don't stack on the pin.
    void spawnSimulators({
      vehicleType,
      lat: aor.center[1] + 0.01,
      lon: aor.center[0] + 0.01,
      count: n,
    })
      .then((result) => {
        if (result.ok) {
          setMessage({ text: `Spawned ${result.data.added.join(', ')}`, warn: false });
        } else {
          setMessage({ text: result.error.message, warn: true });
        }
      })
      .finally(() => setBusy(false));
  };

  return (
    <div className="flex flex-col gap-2">
      {isAdmin ? (
        <div className="glass-2 flex flex-col gap-2 rounded-[12px] p-3">
          <span className="label-mono text-[9px]">SPAWN SIMULATOR — AOR {aor.id}</span>
          <div className="flex items-center gap-1.5">
            <select
              value={vehicleType}
              onChange={(event) => setVehicleType(event.target.value)}
              aria-label="Simulator vehicle type"
              className="well h-8 min-w-0 flex-1 rounded-[99px] px-3 label-mono text-[9px] outline-none"
            >
              {SIM_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            <input
              value={count}
              onChange={(event) => setCount(event.target.value)}
              inputMode="numeric"
              aria-label="How many to spawn"
              className={cn(
                'well tnum h-8 w-11 rounded-[99px] text-center text-[12px] outline-none',
                !countValid && 'border border-[color:var(--danger)]',
              )}
            />
            <button
              type="button"
              disabled={busy || !countValid}
              onClick={spawn}
              className="h-8 rounded-[99px] bg-white/90 px-3.5 label-mono text-[9px] text-[#0c0c0d] disabled:opacity-40"
            >
              SPAWN
            </button>
          </div>
          {message ? (
            <p
              className="text-[10px]"
              style={{ color: message.warn ? 'var(--caution)' : 'var(--ok)' }}
            >
              {message.text}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="glass-2 rounded-[12px] p-3">
          <p className="text-[11px] text-[color:var(--muted-foreground-3)]">
            Spawning simulators needs admin access. Boot-time seeding stays available via{' '}
            <span className="tnum">SIM_DRONES</span>.
          </p>
        </div>
      )}
      <div className="flex flex-col gap-1">
        {ids.map((id) => (
          <SimRow key={id} droneId={id} canRemove={isAdmin} />
        ))}
      </div>
      <p className="px-1 text-[9.5px] leading-relaxed text-[color:var(--muted-foreground-3)]">
        A simulated track on a live display is a deception risk, so every one is badged SIM here, in
        the fleet, and on the map. Spawns are admin-only and audited.
      </p>
    </div>
  );
}

function SimRow({ droneId, canRemove }: { droneId: DroneId; canRemove: boolean }) {
  const identity = useDroneIdentity(droneId);
  const [busy, setBusy] = useState(false);
  if (!identity?.simulated) return null;
  return (
    <div className="well flex items-center gap-2 rounded-[11px] px-3 py-2">
      <span className="size-1.5 rounded-full px-pulse" style={{ background: 'var(--caution)' }} />
      <span className="tnum text-[12px]">{identity.callsign}</span>
      <span className="label-mono text-[8px] text-[color:var(--caution)]">SIM</span>
      {canRemove ? (
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            void removeSimulator(droneId).finally(() => setBusy(false));
          }}
          className="ml-auto label-mono text-[8px] text-[color:var(--muted-foreground-3)] hover:text-[color:var(--danger-foreground)] disabled:opacity-40"
        >
          REMOVE
        </button>
      ) : (
        <span className="ml-auto" />
      )}
    </div>
  );
}

function RoadmapCard({ title, status, body }: { title: string; status: string; body: string }) {
  return (
    <div className="glass-2 flex flex-col gap-2 rounded-[14px] p-4">
      <div className="flex items-center gap-2">
        <span className="label-mono text-[9px]">{title}</span>
        <span
          className="ml-auto rounded-[99px] px-2 py-0.5 label-mono text-[8px]"
          style={{ color: 'var(--muted-foreground-3)', background: 'oklch(1 0 0 / 0.06)' }}
        >
          {status}
        </span>
      </div>
      <p className="text-[11px] text-[color:var(--muted-foreground-3)]">{body}</p>
    </div>
  );
}

/**
 * Cursor-on-Target feed status. The endpoint is configured in the deployment
 * env (COT_ENDPOINT), not here: where the COP flows is a decision for whoever
 * holds the box, not a toggle any operator can flip.
 */
export function CotTab() {
  const [status, setStatus] = useState<CotStatus | null>(null);

  useEffect(() => {
    const load = () =>
      void getCotStatus().then((result) => {
        if (result.ok) setStatus(result.data);
      });
    load();
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, []);

  if (!status) return null;

  if (!status.configured) {
    return (
      <RoadmapCard
        title="CURSOR-ON-TARGET / TAK"
        status="NOT CONFIGURED"
        body="Set COT_ENDPOINT (udp:239.2.3.1:6969 for ATAK mesh SA, tcp:takserver:8087, or tls:takserver:8089 with a client .p12) and the fleet appears on every ATAK / WinTAK / iTAK screen alongside manned units. Configured in the deployment env, not the UI."
      />
    );
  }

  const healthy = !status.lastError;
  const lastAge =
    status.lastSentAt != null ? Math.max(0, Math.round(Date.now() / 1000 - status.lastSentAt)) : null;

  return (
    <div className="flex flex-col gap-2.5 py-1">
      <div className="flex items-center gap-2">
        <span className="label-mono text-[9px]">CURSOR-ON-TARGET / TAK</span>
        <span
          className="ml-auto label-mono text-[8px]"
          style={{ color: healthy ? 'var(--ok)' : 'var(--caution)' }}
        >
          ● {healthy ? 'EMITTING' : 'DEGRADED'}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Field label="ENDPOINT" value={status.endpoint ?? '—'} />
        {/* Beside the endpoint because the two are set together and the wrong
            pairing is silent: this emitter does not negotiate, so a peer on the
            other version reads the bytes as garbage and shows nothing. */}
        <Field
          label="PROTOCOL"
          value={status.format === 'protobuf' ? 'TAK v1 protobuf' : 'TAK v0 XML'}
        />
        <Field label="EVENTS SENT" value={String(status.eventsSent ?? 0)} />
        <Field label="LAST EMIT" value={lastAge === null ? 'never' : `${lastAge}s ago`} />
        <Field label="STALE AFTER" value="10 s" />
      </div>
      {status.tls ? <CotTlsStatusBlock tls={status.tls} nowS={Date.now() / 1000} /> : null}
      {status.lastError ? (
        <p className="text-[10.5px]" style={{ color: 'var(--caution)' }}>
          {status.lastError}
        </p>
      ) : null}
      <p className="text-[9.5px] leading-relaxed text-[color:var(--muted-foreground-3)]">
        Every positional track is emitted as CoT so it renders on any TAK client. Tracks go stale on
        the receiver 10 s after the feed stops — a dead link cannot leave ghosts on someone's map.
      </p>
    </div>
  );
}

/**
 * The AI duty officer. Advisory by construction — the backend has no code path
 * from a model reply to a vehicle, so everything here is read-and-recommend.
 * TRIAGE runs a structured risk pass over the live fleet; the ask box is
 * free-form with the fleet snapshot as context.
 */
interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  meta?: string;
  alerts?: AiAlert[];
  error?: boolean;
}

function AiTab() {
  const [status, setStatus] = useState<AiStatus | null>(null);
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState<'ask' | 'triage' | 'brief' | null>(null);
  const [thread, setThread] = useState<ChatMessage[]>([]);
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    void getAiStatus().then((result) => {
      if (result.ok) setStatus(result.data);
    });
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'nearest' });
  }, [thread, busy]);

  const runTriage = () => {
    setBusy('triage');
    setThread((t) => [...t, { role: 'user', content: 'Triage the fleet.' }]);
    void triageFleet().then((result) => {
      setBusy(null);
      setThread((t) => [
        ...t,
        result.ok
          ? {
              role: 'assistant',
              content:
                result.data.alerts.length === 0
                  ? 'No risks flagged across the fleet.'
                  : (result.data.raw ?? `${result.data.alerts.length} risk(s) flagged:`),
              alerts: result.data.alerts,
              meta: `${result.data.model} · ${(result.data.latencyMs / 1000).toFixed(1)}s`,
            }
          : { role: 'assistant', content: result.error.message, error: true },
      ]);
    });
  };

  const runBrief = () => {
    setBusy('brief');
    setThread((t) => [...t, { role: 'user', content: 'Brief me.' }]);
    void getBriefing().then((result) => {
      setBusy(null);
      setThread((t) => [
        ...t,
        result.ok
          ? {
              role: 'assistant',
              content: result.data.briefing,
              meta: `${result.data.model} · ${(result.data.latencyMs / 1000).toFixed(1)}s`,
            }
          : { role: 'assistant', content: result.error.message, error: true },
      ]);
    });
  };

  const voice = useVoiceInput((text) => setQuestion((q) => (q ? `${q} ${text}` : text)));

  const runAsk = () => {
    const q = question.trim();
    if (!q || busy !== null) return;
    setBusy('ask');
    setQuestion('');
    // History = the conversation so far, minus error turns, mapped to what the
    // server accepts. Captured before the optimistic append.
    const history = thread
      .filter((m) => !m.error)
      .map((m) => ({ role: m.role, content: m.content }));
    setThread((t) => [...t, { role: 'user', content: q }]);
    void askAi(q, history).then((result) => {
      setBusy(null);
      setThread((t) => [
        ...t,
        result.ok
          ? {
              role: 'assistant',
              content: result.data.answer,
              meta: `${result.data.model} · ${(result.data.latencyMs / 1000).toFixed(1)}s`,
            }
          : { role: 'assistant', content: result.error.message, error: true },
      ]);
    });
  };

  if (status && !status.configured) {
    return (
      <RoadmapCard
        title="AI DUTY OFFICER"
        status="NOT CONFIGURED"
        body="Set OLLAMA_API_KEY (cloud) or point OLLAMA_BASE_URL at a local Ollama instance to enable fleet triage, plan critique and flight summaries. Advisory only — the model can never command an aircraft."
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-2.5 py-1">
      <div className="flex shrink-0 items-center gap-2">
        <span className="label-mono text-[9px]">AI DUTY OFFICER</span>
        <span className="ml-auto label-mono text-[8px] text-[color:var(--muted-foreground-3)]">
          {status?.model ?? '…'} · ADVISORY
        </span>
        {thread.length ? (
          <button
            type="button"
            onClick={() => setThread([])}
            className="label-mono text-[8px] text-[color:var(--muted-foreground-3)] hover:text-foreground"
          >
            CLEAR
          </button>
        ) : null}
      </div>

      <button
        type="button"
        onClick={runTriage}
        disabled={busy !== null}
        className="shrink-0 rounded-[99px] bg-[oklch(1_0_0/0.92)] py-2 label-mono text-[9px] text-[#0c0c0d] disabled:opacity-40"
      >
        {busy === 'triage' ? 'TRIAGING FLEET…' : '⚚ TRIAGE FLEET'}
      </button>
      <button
        type="button"
        onClick={runBrief}
        disabled={busy !== null}
        className="shrink-0 rounded-[99px] border border-[color:var(--border)] px-3 py-1.5 label-mono text-[9px] disabled:opacity-40"
      >
        {busy === 'brief' ? 'BRIEFING…' : '▤ BRIEF ME'}
      </button>

      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto">
        {thread.length === 0 ? (
          <p className="px-1 py-2 text-[10.5px] leading-relaxed text-[color:var(--muted-foreground-3)]">
            Ask anything about the live fleet — the officer answers with the current COP as
            context, and follow-ups keep the thread.
          </p>
        ) : (
          <div className="flex flex-col gap-1.5 pb-1">
            {thread.map((message, index) =>
              message.role === 'user' ? (
                <div
                  key={index}
                  className="ml-6 self-end rounded-[12px] bg-white/10 px-3 py-2 text-[11px] leading-relaxed"
                >
                  {message.content}
                </div>
              ) : (
                <div
                  key={index}
                  className="mr-3 rounded-[12px] border border-[color:var(--border-nested)] bg-[oklch(0_0_0/0.22)] px-3 py-2.5"
                  style={
                    message.error
                      ? { borderColor: 'color-mix(in oklch, var(--danger) 45%, transparent)' }
                      : undefined
                  }
                >
                  <p className="whitespace-pre-wrap text-[11px] leading-relaxed">{message.content}</p>
                  {message.alerts?.length ? (
                    <div className="mt-1.5 flex flex-col gap-1.5">
                      {message.alerts.map((alert, alertIndex) => (
                        <div
                          key={alertIndex}
                          className="rounded-[10px] border px-2.5 py-1.5"
                          style={{
                            borderColor: `color-mix(in oklch, var(${alert.severity === 'critical' ? '--danger' : '--caution'}) 45%, transparent)`,
                            background: `color-mix(in oklch, var(${alert.severity === 'critical' ? '--danger' : '--caution'}) 7%, transparent)`,
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <span className="tnum text-[11px] font-semibold">{alert.id}</span>
                            <span
                              className="label-mono text-[7.5px]"
                              style={{ color: `var(${alert.severity === 'critical' ? '--danger-foreground' : '--caution'})` }}
                            >
                              {alert.severity.toUpperCase()}
                            </span>
                          </div>
                          <p className="mt-0.5 text-[10.5px] text-[color:var(--muted-foreground-2)]">{alert.reason}</p>
                          <p className="mt-0.5 text-[10px] text-[color:var(--muted-foreground-3)]">→ {alert.action}</p>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {message.meta ? (
                    <p className="mt-1.5 label-mono text-[7.5px] text-[color:var(--muted-foreground-3)]">
                      {message.meta}
                    </p>
                  ) : null}
                </div>
              ),
            )}
            {busy !== null ? (
              <div className="mr-3 rounded-[12px] border border-[color:var(--border-nested)] bg-[oklch(0_0_0/0.22)] px-3 py-2.5">
                <span className="px-pulse text-[11px] text-[color:var(--muted-foreground-3)]">
                  {busy === 'triage' ? 'Reading the fleet…' : 'Thinking…'}
                </span>
              </div>
            ) : null}
            <div ref={endRef} />
          </div>
        )}
      </div>

      <div className="flex shrink-0 gap-1.5">
        <input
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') runAsk();
          }}
          placeholder="Ask about the fleet…"
          className="well h-8 min-w-0 flex-1 rounded-[99px] px-3 text-[11px] outline-none placeholder:text-[color:var(--muted-foreground-3)]"
        />
        {voice.supported ? (
          <button
            type="button"
            onClick={() => (voice.listening ? voice.stop() : voice.start())}
            title={voice.listening ? 'Listening… tap to stop' : 'Dictate a question'}
            aria-label="Dictate"
            className="rounded-[99px] border border-[color:var(--border)] px-3 label-mono text-[11px] disabled:opacity-40"
            style={voice.listening ? { color: 'var(--danger-foreground)' } : undefined}
          >
            {voice.listening ? '● REC' : 'MIC'}
          </button>
        ) : null}
        <button
          type="button"
          onClick={runAsk}
          disabled={busy !== null || !question.trim()}
          className="rounded-[99px] border border-[color:var(--border)] px-3 label-mono text-[9px] disabled:opacity-40"
        >
          {busy === 'ask' ? '…' : 'ASK'}
        </button>
      </div>

      <p className="shrink-0 text-[9.5px] leading-relaxed text-[color:var(--muted-foreground-3)]">
        Advisory only. The model reads fleet state and never commands an aircraft. Cloud endpoint —
        keep to the public/open-data tier, or point at a local Ollama at the edge.
      </p>
    </div>
  );
}

/** Minimap viewBox. Wide and short, like the design's OVERVIEW panel. */
const MINI_W = 300;
const MINI_H = 96;
/** How much beyond the AOR boundary the minimap shows, for context. */
const MINI_PAD = 1.35;

/**
 * The situational overview from the design: the AOR boundary, every track in
 * it as a dot, and the main map's current viewport as a rectangle. Clicking
 * pans the main map there. Redrawn on a slow interval — this is a glance
 * surface, not a second tactical display.
 */
function OverviewMiniMap() {
  const controller = useMapController();
  const aorId = useWorkspaceStore((s) => s.aor);
  const [dots, setDots] = useState<Array<{ id: string; x: number; y: number; color: string }>>([]);
  const [view, setView] = useState<{ x: number; y: number; w: number; h: number } | null>(null);

  const aor = aorById(aorId);
  const [clon, clat] = aor.center;
  const dLat = (AOR_RADIUS_KM * MINI_PAD) / 111;
  const dLon = (AOR_RADIUS_KM * MINI_PAD) / (111 * Math.cos((clat * Math.PI) / 180));
  const toX = (lon: number) => ((lon - (clon - dLon)) / (2 * dLon)) * MINI_W;
  const toY = (lat: number) => MINI_H - ((lat - (clat - dLat)) / (2 * dLat)) * MINI_H;

  // The AOR boundary square (unpadded radius) in minimap coordinates.
  const bLat = AOR_RADIUS_KM / 111;
  const bLon = AOR_RADIUS_KM / (111 * Math.cos((clat * Math.PI) / 180));
  const box = {
    x: toX(clon - bLon),
    y: toY(clat + bLat),
    w: toX(clon + bLon) - toX(clon - bLon),
    h: toY(clat - bLat) - toY(clat + bLat),
  };

  useEffect(() => {
    const compute = () => {
      const next: Array<{ id: string; x: number; y: number; color: string }> = [];
      for (const drone of useDroneStore.getState().drones.values()) {
        const { lat, lon } = drone.scalars;
        if (lat === undefined || lon === undefined || (lat === 0 && lon === 0)) continue;
        const x = toX(lon);
        const y = toY(lat);
        if (x < 0 || x > MINI_W || y < 0 || y > MINI_H) continue;
        next.push({ id: drone.droneId, x, y, color: teamColorFor(drone.droneId) ?? 'var(--friend)' });
      }
      setDots(next);

      const map = controller.raw();
      if (map) {
        const bounds = map.getBounds();
        const x1 = Math.max(0, Math.min(MINI_W, toX(bounds.getWest())));
        const x2 = Math.max(0, Math.min(MINI_W, toX(bounds.getEast())));
        const y1 = Math.max(0, Math.min(MINI_H, toY(bounds.getNorth())));
        const y2 = Math.max(0, Math.min(MINI_H, toY(bounds.getSouth())));
        // A viewport that covers (or misses) the whole box tells you nothing.
        if (x2 - x1 > 2 && y2 - y1 > 2 && (x2 - x1 < MINI_W - 2 || y2 - y1 < MINI_H - 2)) {
          setView({ x: x1, y: y1, w: x2 - x1, h: y2 - y1 });
        } else {
          setView(null);
        }
      }
    };
    compute();
    const timer = window.setInterval(compute, 900);
    return () => window.clearInterval(timer);
    // toX/toY are derived from aorId; recreating them is the point of the dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controller, aorId]);

  const panTo = (event: React.MouseEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const fx = (event.clientX - rect.left) / rect.width;
    const fy = (event.clientY - rect.top) / rect.height;
    const lon = clon - dLon + fx * 2 * dLon;
    const lat = clat + dLat - fy * 2 * dLat;
    controller.raw()?.easeTo({ center: [lon, lat], duration: 500 });
  };

  return (
    <div className="shrink-0 px-2 pt-2">
      <div className="well overflow-hidden rounded-[14px]">
        <div className="flex items-center px-3 pt-2">
          <span className="label-mono text-[8.5px]">OVERVIEW</span>
          <span className="ml-auto label-mono text-[8.5px] text-[color:var(--muted-foreground-3)]">
            AOR {aor.id}
          </span>
        </div>
        <svg
          viewBox={`0 0 ${MINI_W} ${MINI_H}`}
          className="block w-full cursor-crosshair"
          style={{ aspectRatio: `${MINI_W} / ${MINI_H}` }}
          onClick={panTo}
          role="img"
          aria-label={`Overview of AOR ${aor.id} with ${dots.length} tracks`}
        >
          <rect
            x={box.x}
            y={box.y}
            width={box.w}
            height={box.h}
            fill="none"
            stroke="rgba(255,255,255,0.35)"
            strokeWidth="1"
            strokeDasharray="4 3"
          />
          {dots.map((dot) => (
            <circle key={dot.id} cx={dot.x} cy={dot.y} r="2.4" fill={dot.color} />
          ))}
          {view ? (
            <rect
              x={view.x}
              y={view.y}
              width={view.w}
              height={view.h}
              fill="rgba(255,255,255,0.06)"
              stroke="rgba(255,255,255,0.8)"
              strokeWidth="1.2"
            />
          ) : null}
        </svg>
      </div>
    </div>
  );
}

/** The per-parameter result of one doctrine apply: what was written, what stopped it. */
function FailsafeOutcome({ result }: { result: FailsafeApplyResult }) {
  const summary = summarizeApply(result);
  return (
    <div className="glass-2 rounded-[12px]">
      <div className="flex items-center gap-2 px-3 py-2">
        <span className="label-mono text-[9px]">APPLY RESULT</span>
        <span
          className="ml-auto rounded-[99px] px-2 py-0.5 label-mono text-[8.5px]"
          style={{
            color: summary.ok ? 'var(--ok)' : 'var(--danger-foreground)',
            background: summary.ok
              ? 'color-mix(in oklch, var(--ok) 16%, transparent)'
              : 'color-mix(in oklch, var(--danger) 18%, transparent)',
          }}
        >
          {summary.ok ? 'APPLIED' : 'PARTIAL'}
        </span>
      </div>
      <p className="px-3 pb-1.5 text-[10.5px] leading-relaxed text-[color:var(--muted-foreground-2)]">
        {summary.line}
      </p>
      <ul className="scroll-thin max-h-32 overflow-y-auto border-t border-[color:var(--border-nested)]">
        {result.applied.map((outcome) => (
          <li key={outcome.name} className="flex items-start gap-2 px-3 py-1.5 text-[10.5px]">
            <span className="mt-px shrink-0" style={{ color: 'var(--ok)' }}>
              ✓
            </span>
            <span className="tnum shrink-0">{outcome.name}</span>
          </li>
        ))}
        {result.failed ? (
          <li
            className="flex items-start gap-2 px-3 py-1.5 text-[10.5px]"
            style={{ color: 'var(--danger-foreground)' }}
          >
            <span className="mt-px shrink-0">✗</span>
            <span className="tnum shrink-0">{result.failed.name}</span>
            <span className="min-w-0 flex-1">{result.failed.message}</span>
          </li>
        ) : null}
      </ul>
      {result.warnings.length ? (
        <div className="border-t border-[color:var(--border-nested)] px-3 py-2">
          {result.warnings.map((w) => (
            <p key={w} className="text-[10px] leading-relaxed" style={{ color: 'var(--caution)' }}>
              ⚠ {w}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Set a pre-flight failsafe doctrine on one aircraft.
 *
 * `current`/`currentDetail` come from the aircraft's own FS_* parameters
 * (`_read_current` on the server) -- read-back, not a guess. A doctrine that
 * disables the return-to-operator failsafes still goes through a confirm card
 * first, mirroring the server's own confirm=true gate so the operator
 * acknowledges it before the call, not after a rejection.
 */
function DroneFailsafe({
  droneId,
  current,
  currentDetail,
  available,
}: {
  droneId: DroneId;
  current: string | null;
  currentDetail: string;
  available: FailsafeDoctrine[];
}) {
  const canControl = useCanControl(droneId);
  const armed = useDroneScalars(droneId, ['armed']).armed === true;
  const [confirming, setConfirming] = useState<FailsafeDoctrine | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [result, setResult] = useState<FailsafeApplyResult | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);

  const run = (doctrine: FailsafeDoctrine, confirm: boolean) => {
    setBusyKey(doctrine.key);
    setConfirming(null);
    setResult(null);
    setApplyError(null);
    void applyFailsafe(droneId, doctrine.key, confirm)
      .then((res) => {
        if (res.ok) setResult(res.data);
        else setApplyError(res.error.message);
      })
      .finally(() => setBusyKey(null));
  };

  const onApply = (doctrine: FailsafeDoctrine) => {
    if (doctrineNeedsConfirm(doctrine)) setConfirming(doctrine);
    else run(doctrine, false);
  };

  if (confirming) {
    return (
      <div
        className="glass-2 flex flex-col gap-2 rounded-[14px] p-4"
        style={{ borderColor: 'color-mix(in oklch, var(--danger) 55%, transparent)' }}
      >
        <span className="label-mono text-[9px]" style={{ color: 'var(--danger-foreground)' }}>
          ⚠ {confirming.label.toUpperCase()}
        </span>
        <p className="text-[11px] leading-relaxed text-[color:var(--muted-foreground-2)]">
          {confirming.description}
        </p>
        <p className="text-[10.5px] leading-relaxed" style={{ color: 'var(--danger-foreground)' }}>
          This disables the autopilot's return-to-operator failsafes. Confirm to send.
        </p>
        <div className="mt-1 flex gap-2">
          <button
            type="button"
            onClick={() => run(confirming, true)}
            className="flex-1 rounded-[99px] py-2 label-mono text-[9px] font-semibold"
            style={{
              color: 'var(--danger-foreground)',
              background: 'color-mix(in oklch, var(--danger) 18%, transparent)',
              border: '1px solid color-mix(in oklch, var(--danger) 55%, transparent)',
            }}
          >
            CONFIRM & APPLY
          </button>
          <button
            type="button"
            onClick={() => setConfirming(null)}
            className="flex-1 rounded-[99px] border border-[color:var(--border)] py-2 label-mono text-[9px]"
          >
            CANCEL
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <span className="label-mono text-[8px] text-[color:var(--muted-foreground-2)]">
          FAILSAFE DOCTRINE
        </span>
        <span className="text-[9.5px] leading-relaxed text-[color:var(--muted-foreground-3)]">
          {currentLine(current, currentDetail, available)} Pre-flight only; disarm to change.
        </span>
      </div>

      {available.map((doctrine) => (
        <div key={doctrine.key} className="well flex flex-col gap-1.5 rounded-[12px] px-3 py-2.5">
          <div className="flex items-center gap-2">
            <span className="text-[12px] font-semibold">{doctrine.label}</span>
            {doctrine.disablesSafety ? (
              <span
                className="rounded-[99px] px-1.5 py-px label-mono text-[7.5px]"
                style={{
                  color: 'var(--danger-foreground)',
                  background: 'color-mix(in oklch, var(--danger) 16%, transparent)',
                }}
              >
                DISABLES FAILSAFES
              </span>
            ) : null}
          </div>
          <p className="text-[10.5px] leading-relaxed text-[color:var(--muted-foreground-3)]">
            {doctrine.description}
          </p>
          {doctrine.caveats.map((caveat) => (
            <p key={caveat} className="text-[9.5px] leading-relaxed" style={{ color: 'var(--caution)' }}>
              ⚠ {caveat}
            </p>
          ))}
          {canControl ? (
            <button
              type="button"
              disabled={armed || busyKey !== null}
              onClick={() => onApply(doctrine)}
              className="mt-0.5 self-start rounded-[99px] bg-[oklch(1_0_0/0.92)] px-4 py-1.5 label-mono text-[9px] text-[#0c0c0d] disabled:opacity-40"
            >
              {busyKey === doctrine.key ? 'APPLYING…' : 'APPLY'}
            </button>
          ) : null}
        </div>
      ))}

      {armed ? (
        <p className="px-1 text-[9.5px]" style={{ color: 'var(--caution)' }}>
          Aircraft is armed — failsafe parameters are read-only in flight. Disarm to change doctrine.
        </p>
      ) : null}
      {!canControl ? (
        <p className="px-1 text-[9.5px] text-[color:var(--muted-foreground-3)]">
          Read-only: setting a doctrine needs operator control of this aircraft.
        </p>
      ) : null}

      {applyError ? (
        <p className="px-1 text-[11px]" style={{ color: 'var(--danger-foreground)' }}>
          {applyError}
        </p>
      ) : null}
      {result ? <FailsafeOutcome result={result} /> : null}
    </div>
  );
}

/** One row of the fleet apply result: the drone and how the doctrine landed on it. */
function FleetResultRow({
  droneId,
  status,
  detail,
}: FleetFailsafeResult['results'][number]) {
  const style = fleetStatusStyle(status);
  return (
    <li className="flex items-start gap-2 px-3 py-1.5 text-[10.5px]">
      <span className="tnum w-20 shrink-0 truncate">{droneId}</span>
      <span
        className="shrink-0 label-mono text-[8px]"
        style={{ color: style.tone }}
      >
        {style.label}
      </span>
      <span className="min-w-0 flex-1 text-[color:var(--muted-foreground-3)]">{detail}</span>
    </li>
  );
}

/**
 * Apply one doctrine across every aircraft the operator can control.
 *
 * The server scopes the fleet by accessible_drones and per-drone control, so a
 * drone out of scope is never named in the result at all -- the copy says as
 * much. Each drone comes back with one of five outcomes; they are rendered
 * with a distinct badge each rather than the binary accepted/refused an
 * OutcomeList would give, so an armed-and-skipped or out-of-scope drone does
 * not read as a failure.
 */
function FleetFailsafe({ available }: { available: FailsafeDoctrine[] }) {
  const canCommand = useHasRole('operator');
  const [confirming, setConfirming] = useState<FailsafeDoctrine | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [result, setResult] = useState<FleetFailsafeResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!canCommand) return null;

  const run = (doctrine: FailsafeDoctrine, confirm: boolean) => {
    setBusyKey(doctrine.key);
    setConfirming(null);
    setResult(null);
    setError(null);
    void applyFleetFailsafe(doctrine.key, confirm)
      .then((res) => {
        if (res.ok) setResult(res.data);
        else setError(res.error.message);
      })
      .finally(() => setBusyKey(null));
  };

  const onApply = (doctrine: FailsafeDoctrine) => {
    if (doctrineNeedsConfirm(doctrine)) setConfirming(doctrine);
    else run(doctrine, false);
  };

  return (
    <div className="flex flex-col gap-2 border-t border-[color:var(--border-nested)] pt-4">
      <span className="label-mono text-[8px] text-[color:var(--muted-foreground-2)]">
        APPLY TO FLEET
      </span>
      <span className="text-[9.5px] leading-relaxed text-[color:var(--muted-foreground-3)]">
        Applies to every aircraft you control. Armed aircraft are skipped (pre-flight only); drones
        outside your access are never touched.
      </span>

      {confirming ? (
        <div
          className="glass-2 flex flex-col gap-2 rounded-[14px] p-4"
          style={{ borderColor: 'color-mix(in oklch, var(--danger) 55%, transparent)' }}
        >
          <span className="label-mono text-[9px]" style={{ color: 'var(--danger-foreground)' }}>
            ⚠ {confirming.label.toUpperCase()} — WHOLE FLEET
          </span>
          <p className="text-[11px] leading-relaxed text-[color:var(--muted-foreground-2)]">
            {confirming.description}
          </p>
          <p className="text-[10.5px] leading-relaxed" style={{ color: 'var(--danger-foreground)' }}>
            This disables return-to-operator failsafes on every aircraft you control. Confirm to send.
          </p>
          <div className="mt-1 flex gap-2">
            <button
              type="button"
              onClick={() => run(confirming, true)}
              className="flex-1 rounded-[99px] py-2 label-mono text-[9px] font-semibold"
              style={{
                color: 'var(--danger-foreground)',
                background: 'color-mix(in oklch, var(--danger) 18%, transparent)',
                border: '1px solid color-mix(in oklch, var(--danger) 55%, transparent)',
              }}
            >
              CONFIRM & APPLY
            </button>
            <button
              type="button"
              onClick={() => setConfirming(null)}
              className="flex-1 rounded-[99px] border border-[color:var(--border)] py-2 label-mono text-[9px]"
            >
              CANCEL
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {available.map((doctrine) => (
            <button
              key={doctrine.key}
              type="button"
              disabled={busyKey !== null}
              onClick={() => onApply(doctrine)}
              className="rounded-[99px] border border-[color:var(--border)] px-3 py-1.5 label-mono text-[8.5px] disabled:opacity-40"
              style={
                doctrine.disablesSafety
                  ? { color: 'var(--danger-foreground)', borderColor: 'color-mix(in oklch, var(--danger) 45%, transparent)' }
                  : undefined
              }
            >
              {busyKey === doctrine.key ? 'APPLYING…' : doctrine.label.toUpperCase()}
            </button>
          ))}
        </div>
      )}

      {error ? (
        <p className="px-1 text-[11px]" style={{ color: 'var(--danger-foreground)' }}>
          {error}
        </p>
      ) : null}
      {result ? (
        <div className="glass-2 rounded-[12px]">
          <div className="flex items-center gap-2 px-3 py-2">
            <span className="label-mono text-[9px]">FLEET · {result.doctrine.toUpperCase()}</span>
            <span className="ml-auto label-mono text-[8px] text-[color:var(--muted-foreground-3)]">
              {result.results.length} AIRCRAFT
            </span>
          </div>
          {result.results.length ? (
            <ul className="scroll-thin max-h-40 overflow-y-auto border-t border-[color:var(--border-nested)]">
              {result.results.map((row) => (
                <FleetResultRow key={row.droneId} {...row} />
              ))}
            </ul>
          ) : (
            <p className="px-3 pb-2 pt-1 text-[10.5px] text-[color:var(--muted-foreground-3)]">
              No aircraft in your control scope.
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The FS tab. The doctrine catalog is drone-independent but only reachable
 * through a drone-scoped GET, so it is fetched once here off the selected
 * aircraft and shared by both the per-drone control and the fleet section.
 */
/**
 * Read and write one parameter.
 *
 * Deliberately one at a time and typed by hand. A searchable dump of every
 * parameter invites scrolling to something that looks adjustable; naming the
 * one you mean is a smaller target. The write control only appears for a role
 * allowed to use it — the server refuses either way, but offering a button
 * that always fails teaches an operator to distrust the UI.
 */
function ParameterBox({ droneId }: { droneId: DroneId }) {
  const canWrite = useHasRole('tactical');
  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const [status, setStatus] = useState<{ text: string; warn: boolean } | null>(null);
  const [busy, setBusy] = useState(false);

  const read = async () => {
    const target = name.trim().toUpperCase();
    if (!target) return;
    setBusy(true);
    const result = await readParameter(droneId, target);
    setBusy(false);
    if (!result.ok) {
      setStatus({ text: result.error.message, warn: true });
      return;
    }
    // A value the aircraft does not hold is reported as such rather than as 0.
    setValue(result.data.value === null ? '' : String(result.data.value));
    setStatus({ text: result.data.message, warn: result.data.value === null });
  };

  const write = async () => {
    const target = name.trim().toUpperCase();
    const next = Number(value);
    if (!target || !Number.isFinite(next)) return;
    setBusy(true);
    const result = await writeParameter(droneId, target, next);
    setBusy(false);
    setStatus(
      result.ok
        ? { text: result.data.result.message, warn: !result.data.result.ok }
        : { text: result.error.message, warn: true },
    );
  };

  return (
    <div className="well flex flex-col gap-2 rounded-[11px] px-3 py-2.5">
      <span className="label-mono text-[8px] text-[color:var(--muted-foreground-2)]">
        PARAMETER
      </span>
      <div className="flex gap-1.5">
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') void read();
          }}
          placeholder="RTL_ALT"
          maxLength={16}
          aria-label="Parameter name"
          className="well h-7 min-w-0 flex-1 rounded-[8px] px-2 font-mono text-[11px] uppercase outline-none"
        />
        <button
          type="button"
          disabled={busy || !name.trim()}
          onClick={() => void read()}
          className="rounded-[8px] border border-[color:var(--border)] px-2.5 label-mono text-[8.5px] disabled:opacity-40"
        >
          READ
        </button>
      </div>
      {canWrite ? (
        <div className="flex gap-1.5">
          <input
            value={value}
            onChange={(event) => setValue(event.target.value)}
            placeholder="value"
            inputMode="decimal"
            aria-label="Parameter value"
            className="well h-7 min-w-0 flex-1 rounded-[8px] px-2 font-mono text-[11px] outline-none"
          />
          <button
            type="button"
            disabled={busy || !name.trim() || !Number.isFinite(Number(value))}
            onClick={() => void write()}
            className="rounded-[8px] border px-2.5 label-mono text-[8.5px] disabled:opacity-40"
            style={{ color: 'var(--caution)', borderColor: 'color-mix(in oklch, var(--caution) 50%, transparent)' }}
          >
            WRITE
          </button>
        </div>
      ) : null}
      {status ? (
        <p
          className="text-[10.5px]"
          style={{ color: status.warn ? 'var(--caution)' : 'var(--muted-foreground-2)' }}
        >
          {status.text}
        </p>
      ) : null}
      {canWrite ? (
        <p className="text-[9.5px] leading-relaxed text-[color:var(--muted-foreground-3)]">
          A parameter write changes how the autopilot behaves with nobody watching. Every write is
          recorded against your account.
        </p>
      ) : null}
    </div>
  );
}

function FailsafeTab({ droneId }: { droneId: DroneId | null }) {
  const [info, setInfo] = useState<FailsafeInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!droneId) {
      setInfo(null);
      setLoadError(null);
      return;
    }
    setInfo(null);
    setLoadError(null);
    void getFailsafe(droneId).then((res) => {
      if (res.ok) setInfo(res.data);
      else setLoadError(res.error.message);
    });
  }, [droneId]);

  if (!droneId) {
    return (
      <p className="px-1 py-6 text-[11px] text-[color:var(--muted-foreground)]">
        Select an aircraft to set its failsafe doctrine.
      </p>
    );
  }
  if (loadError) {
    return (
      <p className="px-1 py-4 text-[11px]" style={{ color: 'var(--caution)' }}>
        {loadError}
      </p>
    );
  }
  if (!info) {
    return (
      <p className="px-1 py-4 text-[11px] text-[color:var(--muted-foreground-3)]">
        Loading doctrines…
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Keyed on the drone so a change resets the per-drone apply state -- a
          prior aircraft's result must never appear under the new one. */}
      <DroneFailsafe
        key={droneId}
        droneId={droneId}
        current={info.current}
        currentDetail={info.currentDetail}
        available={info.available}
      />
      <FleetFailsafe available={info.available} />
      {/* Sits with failsafe because that is what parameters mostly are: the
          doctrine above writes a fixed set of them, and this is the way to
          check what actually landed, or reach one the doctrine does not cover. */}
      <ParameterBox key={`param-${droneId}`} droneId={droneId} />
    </div>
  );
}

export function RightRail() {
  const [tab, setTab] = useState<TabId>('track');
  const primaryId = usePrimarySelectedId();
  // Here rather than in a tab so other nodes stay on the map whichever tab is
  // open; the rail lives as long as Command does.
  useRemoteNodeLayer();

  return (
    <div className="rr-panel glass flex h-full w-[var(--rail-r-w)] max-w-full flex-col overflow-hidden rounded-[20px]">
      <OverviewMiniMap />
      <div className="shrink-0 p-2">
        {/* Ten labels do not fit 330px at any legible size; squeezed with
            flex-1 they overflowed the pill and the last three were cut off
            with no way to reach them. They scroll sideways instead. */}
        <div
          role="tablist"
          className="well flex overflow-x-auto rounded-[99px] p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={(event) => {
                setTab(t.id);
                event.currentTarget.scrollIntoView({ block: 'nearest', inline: 'nearest' });
              }}
              className={cn(
                'min-w-[44px] flex-1 shrink-0 rounded-[99px] px-2 py-1.5 label-mono text-[9px] whitespace-nowrap transition-colors pointer-coarse:py-3',
                tab === t.id ? 'bg-[oklch(1_0_0/0.92)] text-[#0c0c0d]' : 'text-[color:var(--muted-foreground-3)] hover:text-foreground',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto px-3 pb-3">
        {tab === 'track' ? (
          primaryId ? (
            <TrackTab droneId={primaryId} />
          ) : (
            <p className="px-1 py-6 text-[11px] text-[color:var(--muted-foreground)]">
              Select an aircraft to inspect its track.
            </p>
          )
        ) : null}
        {tab === 'layers' ? <LayersTab /> : null}
        {tab === 'fs' ? <FailsafeTab droneId={primaryId ?? null} /> : null}
        {tab === 'sim' ? <SimTab /> : null}
        {/* Both directions of the same connector. What we push onto somebody
            else's COP and what theirs pushes onto ours are one question an
            operator asks, and splitting them across two tabs is how a feed
            nobody remembered gets planned around. */}
        {tab === 'cot' ? (
          <>
            <CotTab />
            <div className="my-2 h-px bg-[color:var(--border-nested)]" />
            <ExternalSourcesPanel />
            <div className="my-2 h-px bg-[color:var(--border-nested)]" />
            {/* Beside the feeds rather than on its own tab: both answer "what
                is on my map that I did not put there", and an operator hunting
                a boundary should not have to guess which tab it lives under. */}
            <OverlaysPanel />
            <RemoteNodesPanel />
          </>
        ) : null}
        {tab === 'ai' ? <AiTab /> : null}
        {tab === 'log' ? <LogTab /> : null}
        {tab === 'video' ? <VideoTab droneId={primaryId ?? null} /> : null}
        {tab === 'search' ? <ImpactTab droneId={primaryId ?? null} /> : null}
        {tab === 'wx' ? <WeatherTab /> : null}
      </div>
    </div>
  );
}

export default RightRail;
